import { useState, useEffect, useRef } from "react";
import { MessageSquare, Send, X, Loader2, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface Props {
  restaurantId: string;
}

const SupportChat = ({ restaurantId }: Props) => {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [conversations, setConversations] = useState<any[]>([]);
  const [messages, setMessages] = useState<any[]>([]);
  const [selectedConv, setSelectedConv] = useState<string | null>(null);
  const [newMessage, setNewMessage] = useState("");
  const [subject, setSubject] = useState("");
  const [sending, setSending] = useState(false);
  const [creating, setCreating] = useState(false);
  const [showNewConv, setShowNewConv] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open || !restaurantId) return;
    loadConversations();

    const channel = supabase
      .channel("restaurant-support-" + restaurantId)
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "support_messages" }, (payload) => {
        setMessages(prev => [...prev, payload.new as any]);
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [open, restaurantId]);

  useEffect(() => {
    if (selectedConv) loadMessages(selectedConv);
  }, [selectedConv]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const loadConversations = async () => {
    const { data } = await supabase.from("support_conversations")
      .select("*").eq("restaurant_id", restaurantId).order("updated_at", { ascending: false });
    setConversations(data || []);
    if (data && data.length > 0 && !selectedConv) setSelectedConv(data[0].id);
  };

  const loadMessages = async (convId: string) => {
    const { data } = await supabase.from("support_messages")
      .select("*").eq("conversation_id", convId).order("created_at", { ascending: true });
    setMessages(data || []);
  };

  const createConversation = async () => {
    if (!subject.trim()) return;
    setCreating(true);
    const { data, error } = await supabase.from("support_conversations")
      .insert({ restaurant_id: restaurantId, subject: subject.trim() })
      .select().single();
    if (error) toast({ title: "Erro", variant: "destructive" });
    else {
      setConversations(prev => [data, ...prev]);
      setSelectedConv(data.id);
      setShowNewConv(false);
      setSubject("");
    }
    setCreating(false);
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedConv) return;
    setSending(true);
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { setSending(false); return; }

    const { error } = await supabase.from("support_messages").insert({
      conversation_id: selectedConv,
      sender_id: user.id,
      sender_type: "restaurant",
      content: newMessage.trim(),
    });
    if (error) toast({ title: "Erro ao enviar", variant: "destructive" });
    else setNewMessage("");
    setSending(false);
  };

  const convStatus = conversations.find(c => c.id === selectedConv)?.status;

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => setOpen(!open)}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-primary text-primary-foreground shadow-lg flex items-center justify-center hover:scale-105 transition-transform"
      >
        {open ? <X className="h-6 w-6" /> : <MessageSquare className="h-6 w-6" />}
      </button>

      {/* Chat window */}
      {open && (
        <div className="fixed bottom-24 right-6 z-50 w-[380px] h-[500px] rounded-2xl border border-border bg-card shadow-2xl flex flex-col overflow-hidden">
          <div className="p-4 border-b border-border bg-primary text-primary-foreground">
            <h3 className="font-display font-bold text-sm">Suporte MenuPro</h3>
            <p className="text-[10px] opacity-80">Estamos aqui para ajudar</p>
          </div>

          {/* Conversation selector */}
          <div className="p-2 border-b border-border flex items-center gap-2 overflow-x-auto">
            {conversations.map(c => (
              <button
                key={c.id}
                onClick={() => setSelectedConv(c.id)}
                className={`shrink-0 px-3 py-1 rounded-full text-[10px] font-medium transition-colors ${
                  selectedConv === c.id ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"
                }`}
              >
                {c.subject.slice(0, 20)}
              </button>
            ))}
            <button onClick={() => setShowNewConv(true)} className="shrink-0 p-1 rounded-full hover:bg-secondary">
              <Plus className="h-4 w-4 text-muted-foreground" />
            </button>
          </div>

          {showNewConv && (
            <div className="p-3 border-b border-border bg-secondary/50">
              <p className="text-xs font-medium mb-2">Nova conversa</p>
              <div className="flex gap-2">
                <Input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Assunto..." className="h-8 text-xs" />
                <Button onClick={createConversation} disabled={creating} size="sm" className="h-8 text-xs">
                  {creating ? <Loader2 className="h-3 w-3 animate-spin" /> : "Criar"}
                </Button>
              </div>
            </div>
          )}

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {!selectedConv ? (
              <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
                <div className="text-center">
                  <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-20" />
                  <p className="text-xs">Crie uma conversa para começar</p>
                </div>
              </div>
            ) : messages.filter(m => m.conversation_id === selectedConv).length === 0 ? (
              <div className="flex items-center justify-center h-full text-muted-foreground">
                <p className="text-xs">Envie a primeira mensagem</p>
              </div>
            ) : (
              messages.filter(m => m.conversation_id === selectedConv).map(msg => (
                <div key={msg.id} className={`flex ${msg.sender_type === "restaurant" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] rounded-xl px-3 py-2 ${
                    msg.sender_type === "restaurant"
                      ? "bg-primary text-primary-foreground"
                      : "bg-secondary text-foreground"
                  }`}>
                    <p className="text-xs whitespace-pre-wrap">{msg.content}</p>
                    <p className={`text-[8px] mt-0.5 ${msg.sender_type === "restaurant" ? "text-primary-foreground/60" : "text-muted-foreground"}`}>
                      {msg.sender_type === "admin" ? "Suporte" : "Você"} · {new Date(msg.created_at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
                    </p>
                  </div>
                </div>
              ))
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          {selectedConv && convStatus === "open" && (
            <div className="p-3 border-t border-border">
              <div className="flex gap-2">
                <Input
                  value={newMessage}
                  onChange={e => setNewMessage(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), sendMessage())}
                  placeholder="Digite sua mensagem..."
                  className="h-9 text-xs"
                />
                <Button onClick={sendMessage} disabled={sending || !newMessage.trim()} size="sm" className="h-9 px-3 bg-gradient-primary text-primary-foreground">
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          )}
          {convStatus === "closed" && (
            <div className="p-3 border-t border-border text-center">
              <p className="text-xs text-muted-foreground">Esta conversa foi encerrada</p>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default SupportChat;
